import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import SimpleHome from "@/components/simple/SimpleHome";
import api, { mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useMall } from "@/context/MallContext";
import { useEnterShop } from "@/lib/useShop";
import { OpeningSoon, btnChip, btnPrimary, money } from "@/components/simple/product/Fields";

/** The ready-by line for the latest line in a bag (7.6). */
function BagReadyBy({ bag }) {
    const [text, setText] = useState("");
    const last = bag.items[bag.items.length - 1];
    const sel = last?.selections || {};
    const extra = sel.cleanup || sel.customer_supplied || sel.source === "supplied" ? 1 : 0;
    useEffect(() => {
        api.get("/simple/ready-by", { params: { store: bag.slug, ready_in: sel.ready_in || "weeks_2_4", extra_days: extra } })
            .then(({ data }) => setText(data.text)).catch(() => setText(""));
    }, [bag.slug, sel.ready_in, extra]);
    return text ? <p className="text-[16px]" data-testid={`cart-ready-${bag.slug}`}>{text}</p> : null;
}

function Row({ item, onRemove, onEdit }) {
    const note = (item.selections?.note || "").split("\n")[0];
    return (
        <li className="flex gap-3 border-t border-line py-3 first:border-t-0" data-testid="cart-row">
            {item.image && (
                <div className="h-16 w-16 shrink-0 border border-line bg-page">
                    <img src={mediaUrl(item.image)} alt="" className="h-full w-full object-cover" onError={(e) => (e.target.parentElement.style.display = "none")} />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <p className="text-[16px] font-semibold leading-tight" data-testid="cart-row-name">{item.product_name}</p>
                {item.summary && <p className="truncate text-[14px] text-muted-ink" data-testid="cart-row-summary">{item.summary}</p>}
                {note && <p className="truncate text-[14px] text-muted-ink" data-testid="cart-row-note">“{note}”</p>}
                <p className="mt-1 flex gap-4 text-[16px]">
                    {item.edit_path && <button type="button" onClick={onEdit} className="min-h-[44px] underline" data-testid="cart-row-edit">Edit</button>}
                    <button type="button" onClick={onRemove} className="min-h-[44px] underline" data-testid="cart-row-remove">Remove</button>
                </p>
            </div>
            <p className="text-[16px] font-semibold" data-testid="cart-row-price">{money(item.line_total)}</p>
        </li>
    );
}

/** `/cart` in Simple (7.6): one row per line, total, ready-by, Pickup or Ship, one **Pay $X**. Empty = the product grid. */
export default function SimpleCart() {
    const { bags, items, removeItem, subtotal } = useCart();
    const mall = useMall();
    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();
    const [shops, setShops] = useState({});
    const [fulfil, setFulfil] = useState(() => sessionStorage.getItem("checkout_fulfillment") || "pickup");
    const [cancelled, setCancelled] = useState(false);
    useEnterShop(bags.length === 1 ? bags[0].slug : null);

    // Back from Stripe without paying (7.1 #4): the cart is untouched, the unannounced order is cancelled quietly.
    const cancelledId = sp.get("cancelled");
    useEffect(() => {
        if (!cancelledId) return;
        api.post(`/orders/${cancelledId}/abandon`).catch(() => { });
        sessionStorage.removeItem("pending_pay");
        setCancelled(true);
        setSp({}, { replace: true });
    }, [cancelledId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        bags.forEach((b) => {
            if (shops[b.slug]) return;
            api.get(`/stores/${b.slug}`).then(({ data }) => setShops((s) => ({ ...s, [b.slug]: data.store || {} }))).catch(() => setShops((s) => ({ ...s, [b.slug]: {} })));
        });
    }, [bags]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { sessionStorage.setItem("checkout_fulfillment", fulfil); }, [fulfil]);

    const pay = (slug) => navigate(`/checkout/${slug}`, { state: { fulfillment: fulfil } });
    const edit = (item) => navigate(item.edit_path, { state: { editId: item.id } });

    if (items.length === 0) {
        return (
            <Layout title="Cart">
                <div className="mx-auto w-full max-w-4xl px-3 pt-4 sm:px-6" data-testid="cart-empty">
                    <h1 className="text-[28px] font-semibold leading-tight">Your cart is empty.</h1>
                    {cancelled && <p className="mt-2 text-[16px]" data-testid="cart-cancelled-line">No charge was made.</p>}
                    <p className="mt-1 text-[16px] text-muted-ink">Pick something to print:</p>
                </div>
                <SimpleHome store={mall.store} />
            </Layout>
        );
    }

    const one = bags.length === 1;
    return (
        <Layout title="Cart">
            <section className="mx-auto w-full max-w-4xl px-3 py-4 sm:px-6" data-testid="simple-cart">
                <h1 className="text-[28px] font-semibold leading-tight" data-testid="cart-title">Your cart</h1>
                {cancelled && <p className="mt-2 border border-ink px-3 py-2 text-[16px]" data-testid="cart-cancelled-line">No charge was made. Your cart is still here.</p>}
                {bags.map((bag) => {
                    const shop = shops[bag.slug] || {};
                    const both = shop.ships && shop.pickup_enabled !== false;
                    return (
                        <div key={bag.slug} className="mt-4 border border-ink px-3 pb-4" data-testid={`cart-bag-${bag.slug}`}>
                            {!one && <p className="mt-3 text-[14px] text-muted-ink">{bag.name}</p>}
                            <ul className="mt-1">
                                {bag.items.map((it) => <Row key={it.id} item={it} onRemove={() => removeItem(it.id)} onEdit={() => edit(it)} />)}
                            </ul>
                            <div className="mt-2 flex items-baseline justify-between border-t border-ink pt-3">
                                <span className="text-[16px]">Total</span>
                                <span className="text-[28px] font-semibold leading-none" data-testid={`cart-total-${bag.slug}`}>{money(bag.subtotal)}</span>
                            </div>
                            <div className="mt-2"><BagReadyBy bag={bag} /></div>
                            {both && (
                                <div className="mt-3 grid grid-cols-2 gap-2" data-testid="cart-fulfillment">
                                    <button type="button" onClick={() => setFulfil("pickup")} className={btnChip(fulfil === "pickup")} data-testid="cart-pickup">Pickup</button>
                                    <button type="button" onClick={() => setFulfil("ship")} className={btnChip(fulfil === "ship")} data-testid="cart-ship">Ship</button>
                                </div>
                            )}
                            {shop.accepts_cards === false ? <OpeningSoon store={shop} testid={`cart-opening-soon-${bag.slug}`} /> : (
                                <button type="button" onClick={() => pay(bag.slug)} className={`${btnPrimary} mt-4`} data-testid={`cart-pay-${bag.slug}`}>
                                    Pay {money(bag.subtotal)}
                                </button>
                            )}
                        </div>
                    );
                })}
                {!one && <p className="mt-3 text-[14px] text-muted-ink" data-testid="cart-multi-note">Each shop is paid separately — total {money(subtotal)}.</p>}
                <Link to={mall.store && !mall.solo ? `/stores/${mall.store.slug}` : "/"} className="mt-4 flex min-h-[44px] items-center text-[16px] underline" data-testid="cart-keep-shopping">Keep shopping</Link>
            </section>
        </Layout>
    );
}
